import { createRef, Component } from 'react';
import PropTypes from 'prop-types';
import findIndex from 'lodash/findIndex';
import _intersection from 'lodash/intersection';
import uniqBy from 'lodash/uniqBy';
import cx from 'classnames';
import { LinkV2 } from '@zola/zola-ui/src/components/LinkV2';
import { Tag } from '@zola/zola-ui/src/components/Tag';
/**
 * In component public registry repo, we have the same function. We duplicate it in web-registry
 * because component-public-registry is not optimized for code splitting. Import it directly can cause
 * unnecessary bundle size increase. This is a registry specific until function, so we won't move it to
 * zola-helper.
 */
import formatPromoMessage from '../../../../util/promoMessageFormatter';

// Helpers/Utils
import { convertCurrencyFormat } from '../../../../util/currencyFormatter';
import { getProductImage } from '../../../../util/productHelper';

// Components / Assets
import ProductPrice from '../../../product/productDetail/productPrice/ProductPrice';
import QuickView from './QuickView';
import TileSwatchList from './TileSwatchList';
import defaultImage from '../../../../assets/images/no-image-available-300.png';

// Styles
import './ProductTile.less';

const SWATCHES_LIMIT = 5;

class ProductTile extends Component {
  constructor(props) {
    super(props);
    const { defaultProductLookId, suggestedQuantity, productLooks } = props;
    const activeProductLook =
      defaultProductLookId &&
      productLooks[this.getProductLookIndex(defaultProductLookId, productLooks)];
    const quantity = activeProductLook && (suggestedQuantity || 1);
    this.state = {
      quantity,
      isAddedStampVisible: false,
      activeProductLook,
      skuPreview: activeProductLook && activeProductLook.sku_previews[0],
    };
    this.productTile = createRef();
    this.handleQuantityUpdate = this.handleQuantityUpdate.bind(this);
    this.handleCartAdd = this.handleCartAdd.bind(this);
    this.handleRegistryAdd = this.handleRegistryAdd.bind(this);
    this.openPdp = this.openPdp.bind(this);
    this.setProductActiveLook = this.setProductActiveLook.bind(this);
    this.onTileMouseEnter = this.onTileMouseEnter.bind(this);
    this.ACTIONS_MAP = {
      ADD_TO_REGISTRY: this.handleRegistryAdd,
      ADD_TO_CART: this.handleCartAdd,
      SHOW_PDP: this.openPdp,
    };
    // if ProductTile is being used for Frequently Added Together
    // set the initial quantity and skuId for adding both products to cart/registry
    this.updateFatProductData();
  }

  componentDidMount() {
    const { fatHoverCallback } = this.props;
    if (fatHoverCallback) {
      this.productTile.current?.addEventListener('mouseenter', fatHoverCallback);
    }
  }

  componentWillUnmount() {
    const { fatHoverCallback } = this.props;
    if (fatHoverCallback) {
      this.productTile.current?.removeEventListener('mouseenter', fatHoverCallback);
    }
    this.productTile.current?.removeEventListener('mouseenter', this.onTileMouseEnter);
  }

  /**
   * Too avoid attaching mouseenter to each tile, only attach it to the ones
   * that have been addzed to registry and remove it immidiately one it's being triggered
   */
  onTileMouseEnter() {
    this.setState({ isAddedStampVisible: false });
    this.productTile.current?.removeEventListener('mouseenter', this.onTileMouseEnter);
  }

  getProductLookIndex(productLookId, productLooks) {
    return Math.max(findIndex(productLooks, { id: productLookId }), 0);
  }

  setProductActiveLook(productLookId) {
    const { productLooks } = this.props;
    const activeProductLook = productLooks[this.getProductLookIndex(productLookId, productLooks)];
    this.setState(
      {
        activeProductLook,
        skuPreview: activeProductLook && activeProductLook.sku_previews[0],
      },
      this.updateFatProductData
    );
  }

  findCommonPromo(skuPreviews) {
    const promos = skuPreviews.map((n) => n.promo_display_view);
    const results = uniqBy(promos, 'discount_id');
    return results.length === 1 && results[0];
  }

  handleRegistryAdd() {
    const { onRegistryAdd, categoryView, productShippingZones, productShippingExcludedStates } =
      this.props;
    const { activeProductLook, quantity, skuPreview } = this.state;
    const categoryName = categoryView && categoryView.name;
    const promise = onRegistryAdd(
      quantity,
      skuPreview,
      categoryName,
      activeProductLook,
      productShippingZones,
      productShippingExcludedStates
    );
    if (promise) {
      promise.then((response) => {
        if (response && response.added) {
          this.setState({ isAddedStampVisible: true });
          this.productTile.current?.addEventListener('mouseenter', this.onTileMouseEnter);
        }
      });
    }
  }

  handleCartAdd() {
    const { onCartAdd } = this.props;
    const { activeProductLook, quantity, skuPreview } = this.state;
    onCartAdd(quantity, skuPreview, activeProductLook).then((response) => {
      if (response && response.quantity) {
        this.setState({ quantity: response.quantity });
      }
    });
  }

  handleQuantityUpdate(event) {
    if (event.target.value === '') return this.setState({ quantity: event.target.value });
    if (event.target.value <= 0 || event.target.value > 100) return null;
    return this.setState(
      {
        quantity: event.target.value,
      },
      this.updateFatProductData
    );
  }

  updateFatProductData() {
    const { index, fatProductUpdateCallback } = this.props;
    // if user updates swatch for Frequently Added Together product then update parent state with latest sku id
    if (fatProductUpdateCallback) {
      const { skuPreview, quantity } = this.state;
      fatProductUpdateCallback(index, skuPreview.id, quantity);
    }
  }

  openPdp(event) {
    const { handleOpenPdp, collectionKey, preventDefaultOnClick } = this.props;
    const { activeProductLook, skuPreview, quantity } = this.state;

    if (preventDefaultOnClick) event.preventDefault();
    handleOpenPdp(quantity, skuPreview, collectionKey, activeProductLook);
  }

  renderQuickView(actions) {
    const { isKit, gridVersion } = this.props;
    const { quantity, skuPreview } = this.state;
    const activeLookActionsKeys = actions && actions.map((n) => n.key);
    const QUICK_ADD_ACTIONS = ['ADD_TO_REGISTRY', 'ADD_TO_CART'];
    // show quantity field only if Add to registry OR Add to cart actions available
    const hasQuantityField = _intersection(activeLookActionsKeys, QUICK_ADD_ACTIONS).length > 0;
    return (
      <QuickView
        actions={actions}
        isKit={isKit}
        hasQuantityField={hasQuantityField}
        quantity={quantity}
        onQuantityChange={this.handleQuantityUpdate}
        actionsMap={this.ACTIONS_MAP}
        stockMessage={skuPreview && skuPreview.stock_message}
        gridVersion={gridVersion}
      />
    );
  }

  renderPrice(priceRange, referencePrice) {
    const { gridVersion } = this.props;
    const minPriceFractionDigits = (priceRange.min_price / 100) % 1 > 0 ? 2 : 0;
    const LowerBoundPrice = convertCurrencyFormat(
      priceRange.min_price / 100,
      'USD',
      minPriceFractionDigits,
      2
    );
    const hasPriceRange = priceRange.min_price < priceRange.max_price;
    const maxPriceFractionDigits = (priceRange.max_price / 100) % 1 > 0 ? 2 : 0;
    const upperBoundPrice = hasPriceRange
      ? convertCurrencyFormat(priceRange.max_price / 100, 'USD', maxPriceFractionDigits, 2)
      : null;
    return (
      <ProductPrice
        className={cx({ gridVersion })}
        price={LowerBoundPrice}
        upperBoundPrice={upperBoundPrice}
        referencePrice={referencePrice}
      />
    );
  }

  // set promo message for template
  renderPromoMessage() {
    const { activeProductLook } = this.state;
    const promo =
      activeProductLook.sku_previews && this.findCommonPromo(activeProductLook.sku_previews);

    if (!promo) {
      return null;
    }

    if (promo.discount_type === 'COMPLETION') {
      return (
        <div className="pdp-promo-detail">
          <b>{promo.discount_amount}% Off</b> with post-wedding discount{' '}
          <LinkV2
            variant="secondary"
            sizes="extraSmall"
            className="pdp-promo-detail__link"
            target="_blank"
            rel="noopener noreferrer"
            href="/faq/115002837692-does-zola-offer-a-post-wedding-or-completion-discount-"
            onClick={(e) => e.stopPropagation()}
          >
            Details
          </LinkV2>
        </div>
      );
    }

    // this is a normal promo code discount
    const promoMessage = formatPromoMessage(promo);
    return (
      <div className="pdp-promo-detail">
        {promoMessage} <b className="pdp-promo-detail__code text-uppercase">{promo.code}</b>{' '}
        <LinkV2
          variant="secondary"
          sizes="extraSmall"
          className="pdp-promo-detail__link"
          target="_blank"
          rel="noopener noreferrer"
          href={`/discount-terms/${promo.discount_id}`}
          onClick={(e) => e.stopPropagation()}
        >
          Details
        </LinkV2>
      </div>
    );
  }

  renderAddedToRegistryStamp() {
    const { quantity } = this.state;
    return (
      <div className="added-stamp__container">
        <div className="added-stamp">
          <i className="zolaicon zolaicon-checkmark" />
          <span>{quantity} Added</span>
        </div>
      </div>
    );
  }

  renderShipping() {
    const { skuPreview } = this.state;
    return (
      skuPreview &&
      skuPreview.free_shipping && <div className="shop-product-tile__shipping">free shipping</div>
    );
  }

  renderRatings() {
    const { productId, isBVSEOEnable } = this.props;
    return (
      <div
        className="shop-product-tile__product_rating"
        data-bv-seo={isBVSEOEnable}
        data-bv-show="inline_rating"
        data-bv-product-id={productId}
      />
    );
  }

  renderSwatches(swatches, activeProductLook) {
    return (
      <TileSwatchList
        activeProductLookId={activeProductLook && activeProductLook.id}
        limit={SWATCHES_LIMIT}
        swatches={swatches}
        onShowMoreCallback={this.openPdp}
        onSwatchChange={(productLookId) => this.setProductActiveLook(productLookId)}
        showMoreAsNumber
      />
    );
  }

  renderBadge(badge) {
    const badgeUrl = `${badge.image_url}?w=150`;
    return (
      <div className="shop-product-tile__badge">
        <img alt={badge.title} src={badgeUrl} />
      </div>
    );
  }

  renderPersonalizeCTA() {
    return (
      <div className="shop-product-tile__personalize-banner">
        <Tag type="emoji" size="lg" emoji={{ ariaLabel: 'pencil emoji', symbol: '✏️' }}>
          Personalizable
        </Tag>
      </div>
    );
  }

  renderFooter() {
    const { isKit, hasCategoryLink, swatches, productCount, categoryView } = this.props;
    const { activeProductLook } = this.state;
    return (
      <div className="shop-product-tile__footer hidden-xs" onClick={isKit ? this.openPdp : null}>
        {swatches && swatches.length > 0 && this.renderSwatches(swatches, activeProductLook)}
        {hasCategoryLink && categoryView && (
          <LinkV2
            arrow
            variant="primary"
            sizes="extraSmall"
            className="shop-product-tile__category-link"
            href={`/shop/category/${categoryView.key}`}
          >
            More {categoryView.name}
          </LinkV2>
        )}
        {productCount && (
          <div className="shop-product-tile__count">
            <span>
              {productCount} <br /> ITEMS
            </span>
          </div>
        )}
      </div>
    );
  }

  render() {
    const {
      brand,
      collectionKey,
      name,
      image,
      index,
      hasCategoryLink,
      isRectangularImage,
      productCount,
      priceRange,
      referencePrice,
      swatches,
      isAddedStampVisibleOverride,
      getPdpLink,
      eventData,
      categoryChain,
      partnerBoutique,
      isPersonalizable,
      gridVersion,
    } = this.props;
    const { activeProductLook, isAddedStampVisible, skuPreview } = this.state;
    const isProductAdded = isAddedStampVisible || isAddedStampVisibleOverride;
    const actions = activeProductLook && activeProductLook.tile_actions;
    const badge = activeProductLook && activeProductLook.badge;
    const productPriceRange = activeProductLook ? activeProductLook.price_range : priceRange;
    const productReferenceRange = activeProductLook
      ? activeProductLook.reference_price_range
      : referencePrice;
    // some products are missing base image
    const featuredImage =
      getProductImage(activeProductLook, 'base', isRectangularImage) ||
      getProductImage(activeProductLook, 'medium', isRectangularImage);
    const productTileImage = `${image || featuredImage || defaultImage}?w=250`;
    const showFooter = (swatches && swatches.length) || hasCategoryLink || productCount;

    const productTileClasses = cx('shop-product-tile', hasCategoryLink && 'has-footer');
    const imageSectionClasses = cx(
      'shop-product-tile__product-image-section',
      isRectangularImage && 'is-rectangular'
    );
    const id = index > -1 ? `js-shop-product-tile-${index}` : '';
    const pdpLink = getPdpLink(
      collectionKey,
      activeProductLook,
      eventData,
      categoryChain,
      partnerBoutique
    );

    if (gridVersion) {
      return (
        <div id={id} className="shop-product-tile gridVersion" ref={this.productTile}>
          <a className="shop-product-tile__top" href={pdpLink} onClick={this.openPdp}>
            <div className={imageSectionClasses}>
              <div className="shop-product-tile__image-wrapper">
                {badge && this.renderBadge(badge)}
                {isPersonalizable && this.renderPersonalizeCTA()}
                <img
                  className="shop-product-tile__product-image"
                  alt="product"
                  src={productTileImage}
                />
                {productCount && (
                  <div className="shop-product-tile__count">{productCount} items</div>
                )}
                {isProductAdded && this.renderAddedToRegistryStamp()}
              </div>
            </div>
          </a>
          <div className="shop-product-tile__bottom">
            <div className="shop-product-tile__product-body" onClick={this.openPdp}>
              <div className="shop-product-tile__brand">{brand}</div>
              <div className="shop-product-tile__description">{name}</div>
              {swatches && swatches.length > 0 && this.renderSwatches(swatches, activeProductLook)}
              {this.renderRatings()}
              <div className="shop-product-tile__spacer invisible">
                {this.renderQuickView(actions)}
              </div>
              {skuPreview && skuPreview.promo_display_view && this.renderPromoMessage()}
              {this.renderPrice(productPriceRange, productReferenceRange)}
            </div>
            <div className="shop-product-tile__hover-state">
              <div className="shop-product-tile__description" onClick={this.openPdp}>
                {name}
              </div>
              {swatches && swatches.length > 0 && this.renderSwatches(swatches, activeProductLook)}
              <div className="shop-product-tile__spacer" onClick={this.openPdp} />
              {this.renderPrice(productPriceRange, productReferenceRange)}
              {this.renderQuickView(actions)}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div id={id} className={productTileClasses} ref={this.productTile}>
        <div className="shop-product-tile__hoverable_content">
          <a href={pdpLink} onClick={this.openPdp}>
            <div className={imageSectionClasses}>
              <div className="shop-product-tile__image-wrapper">
                {badge && this.renderBadge(badge)}
                {isPersonalizable && this.renderPersonalizeCTA()}
                <img
                  className="shop-product-tile__product-image"
                  alt="product"
                  src={productTileImage}
                />
                {isProductAdded && this.renderAddedToRegistryStamp()}
              </div>
            </div>
          </a>
          <div className="shop-product-tile__product-body">
            {this.renderQuickView(actions)}
            <div onClick={this.openPdp}>
              <div className="shop-product-tile__product-details">
                <div className="shop-product-tile__brand">{brand}</div>
                <div className="shop-product-tile__description">{name}</div>
                {this.renderPrice(productPriceRange, productReferenceRange)}
                {skuPreview && skuPreview.promo_display_view && this.renderPromoMessage()}
                {this.renderShipping()}
                {this.renderRatings()}
              </div>
            </div>
          </div>
        </div>
        {showFooter && this.renderFooter()}
      </div>
    );
  }
}

ProductTile.propTypes = {
  isKit: PropTypes.bool,
  productShippingZones: PropTypes.arrayOf(PropTypes.string),
  productShippingExcludedStates: PropTypes.arrayOf(PropTypes.string),
  isRectangularImage: PropTypes.bool,
  hasCategoryLink: PropTypes.bool,
  brand: PropTypes.string,
  name: PropTypes.string,
  productId: PropTypes.string,
  image: PropTypes.string,
  collectionKey: PropTypes.string,
  defaultProductLookId: PropTypes.string,
  index: PropTypes.number,
  productCount: PropTypes.number,
  suggestedQuantity: PropTypes.number,
  productLooks: PropTypes.arrayOf(PropTypes.shape({})),
  swatches: PropTypes.arrayOf(PropTypes.shape({})),
  handleOpenPdp: PropTypes.func,
  getPdpLink: PropTypes.func,
  onRegistryAdd: PropTypes.func,
  onCartAdd: PropTypes.func,
  priceRange: PropTypes.shape({}),
  referencePrice: PropTypes.shape({}),
  categoryView: PropTypes.shape({}),
  eventData: PropTypes.shape({}),
  partnerBoutique: PropTypes.shape({}),
  categoryChain: PropTypes.arrayOf(PropTypes.shape({})),
  // helpers for FAT (frequently added together)
  isAddedStampVisibleOverride: PropTypes.bool,
  fatProductUpdateCallback: PropTypes.func,
  fatHoverCallback: PropTypes.func,
  // Bazaarvoice helper to disable SEO tracking for using tiles on the PDP
  isBVSEOEnable: PropTypes.bool,
  isPersonalizable: PropTypes.bool,
  gridVersion: PropTypes.bool,
  preventDefaultOnClick: PropTypes.bool,
};

export default ProductTile;
